import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isPaymentEnabled } from "../../config/payment";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import "./abonnement.css";

/* ===============================
   Stripe (publishable) key (env-first)
================================== */
const STRIPE_PK =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_STRIPE_PK) ||
  process.env.VITE_STRIPE_PK ||
  process.env.REACT_APP_STRIPE_PK ||
  "";

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

/* ===============================
   API base (env-first, no localhost)
================================== */
const RAW_API =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE = (
  RAW_API
    ? RAW_API.replace(/\/+$/, "").replace(/\/api$/i, "")
    : (typeof window !== "undefined" && window.location.origin) || ""
);

function Abonnement() {
  const navigate = useNavigate();

  const [loadingUser, setLoadingUser] = useState(true);
  const [userRole, setUserRole] = useState(localStorage.getItem("role") || null);
  const [abonnement, setAbonnement] = useState(null);
  const [proStatus, setProStatus] = useState(null); // "approved" | "pending" | "refused"
  const [, setFetchError] = useState("");

  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [selectedPlanName, setSelectedPlanName] = useState("");
  const [selectedPlanPrice, setSelectedPlanPrice] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectingPlanKey, setSelectingPlanKey] = useState(null);

  // Normalise les variantes possibles de statut venant du back
  const normalizeStatus = (s) => {
    const v = String(s || "").trim().toLowerCase();
    if (["approved", "approuve", "approuvée", "approuvee", "valide", "validée", "validee"].includes(v)) return "approved";
    if (["refused", "rejected", "rejete", "rejetee", "refuse", "refus"].includes(v)) return "refused";
    if (!v || ["pending", "en_attente", "waiting"].includes(v)) return "pending";
    return v; // fallback
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setAbonnement(null);
        setProStatus(null);
        setUserRole(null);
        setLoadingUser(false);
        setFetchError("Pas de token");
        return;
      }
      try {
        setFetchError("");
        const res = await fetch(`${API_BASE}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const user = data?.user ?? data; // supporte { user: ... } ou direct

        const role = user?.role ?? null;
        const abo  = user?.abonnement ?? null;
        const rawStatus = user?.proReview?.status ?? user?.proStatus ?? null;

        setUserRole(role);
        setAbonnement(abo);
        setProStatus(normalizeStatus(rawStatus));

        if (role) localStorage.setItem("role", role);
        if (abo !== undefined && abo !== null) localStorage.setItem("abonnement", abo);
      } catch (e) {
        setFetchError(String(e?.message || e));
        setAbonnement(null);
        setProStatus(null);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, []);

  const handleSelectPlan = async (priceId, planName, planPrice) => {
    const planKey = planName.replace("💎 ", "").toLowerCase();

    if (!isPaymentEnabled()) {
      setSelectingPlanKey(planKey);
      try {
        const token = localStorage.getItem("token") || "";
        const res = await fetch(`${API_BASE}/api/users/update-abonnement`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({ abonnement: planKey }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          window.alert(data.message || "Impossible d’enregistrer la formule.");
          return;
        }
        setAbonnement(planKey);
        localStorage.setItem("abonnement", planKey);
        window.dispatchEvent(new CustomEvent("auth:changed"));
        navigate("/pro/calendar");
      } catch {
        window.alert("Erreur réseau.");
      } finally {
        setSelectingPlanKey(null);
      }
      return;
    }

    setSelectedPriceId(priceId);
    setSelectedPlanName(planName);
    setSelectedPlanPrice(planPrice);
    setShowModal(true);
  };

  const offers = [
    {
      title: "Basic",
      features: [
        "✅ Accès aux rendez-vous",
        "✅ 1 seul profil",
        "❌ Accès aux documents",
        "❌ Création de factures",
        "❌ Profils multiples",
      ],
      price: "99€",
      priceId: "price_1RsS34LCVaigfR3hxVThefFL",
    },
    {
      title: "Standard",
      badge: "⭐ Meilleur choix",
      features: [
        "✅ Accès aux rendez-vous",
        "✅ Accès aux documents",
        "✅ Création de factures",
        "✅ 1 seul profil",
        "❌ Profils multiples",
      ],
      price: "129€",
      priceId: "price_1RsS3cLCVaigfR3hgl7hsxfD",
      highlight: true,
    },
    {
      title: "💎 Premium",
      features: [
        "✅ Accès aux rendez-vous",
        "✅ Accès aux documents",
        "✅ Création de factures",
        "✅ Profils multiples (jusqu’à 5 utilisateurs)",
        "✅ Chaque pro a son propre agenda et ses rendez-vous",
      ],
      price: "199€",
      priceId: "price_1RsS47LCVaigfR3hsPSceJvd",
      highlightPremium: true,
    },
  ];

  if (loadingUser) {
    return <div className="abonnement-page">Chargement…</div>;
  }

  if (userRole !== "pro") {
    return (
      <div className="abonnement-page">
        <h2>Accès réservé aux professionnels</h2>
        <button className="btn" onClick={() => navigate("/")}>Retour</button>
      </div>
    );
  }

  if (proStatus === "refused") {
    return (
      <div className="abonnement-page">
        <h2>Votre profil pro a été refusé</h2>
        <p>Merci de renvoyer les informations et documents manquants.</p>
        <a className="btn" href="/pro/resoumission">Refaire la demande</a>
      </div>
    );
  }

  if (proStatus !== "approved") {
    return (
      <div className="abonnement-page">
        <h2>Vérification de votre profil en cours</h2>
        <p>
          Merci d’attendre la validation de votre profil avant de pouvoir souscrire à un abonnement.
          Cela peut prendre jusqu’à <b>24 heures</b> selon le nombre de demandes.
        </p>
      </div>
    );
  }

  // ✅ Approuvé → les 3 offres ; sans paiement (REACT_APP_PAYMENT_ENABLED=false) : sélection seule
  return (
    <div className="abonnement-page">
      {isPaymentEnabled() ? (
        <div className="security-banner">
          <span className="lock">🔒</span>
          <span>Paiement 100% sécurisé via <b>Stripe</b> — chiffrement SSL & 3D Secure 2.0</span>
        </div>
      ) : (
        <div className="security-banner" style={{ background: "linear-gradient(90deg, #fef3c7, #fde68a)" }}>
          <span className="lock">🧪</span>
          <span>
            <b>Mode test</b> — choisissez Basic, Standard ou Premium : la formule est enregistrée tout de suite,{' '}
            <b>sans paiement</b>. Les droits (documents, profils, etc.) restent ceux de la formule choisie.
          </span>
        </div>
      )}

      <h1 className="abo-title">💼 Abonnement Professionnel</h1>

      <div className="current-abonnement">
        <strong>Votre abonnement actuel : </strong>
        <span className="current-abo-pill">
          {abonnement
            ? abonnement.charAt(0).toUpperCase() + abonnement.slice(1)
            : "Non défini"}
        </span>
      </div>

      <div className="abonnement-options">
        {offers.map((offer) => {
          const internalKey = offer.title.replace("💎 ", "").toLowerCase();
          return (
            <PlanCard
              key={offer.title}
              {...offer}
              paymentEnabled={isPaymentEnabled()}
              isSelecting={selectingPlanKey === internalKey}
              isCurrent={
                !!abonnement &&
                abonnement.toLowerCase() === internalKey
              }
              onSelect={() =>
                handleSelectPlan(
                  offer.priceId,
                  offer.title.replace("💎 ", ""),
                  offer.price
                )
              }
            />
          );
        })}
      </div>

      {isPaymentEnabled() && showModal && selectedPriceId && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <button className="close-button" onClick={() => setShowModal(false)} aria-label="Fermer">✖</button>

            {!stripePromise ? (
              <div className="message error" style={{ marginBottom: 14 }}>
                ❌ Paiement Stripe non configuré. Ajoute la variable d’environnement
                <strong> REACT_APP_STRIPE_PK</strong> (ou <strong>VITE_STRIPE_PK</strong>) sur Cloudflare Pages.
              </div>
            ) : null}

            {/* En-tête sécurité dans la modale */}
            <div className="modal-secure-header">
              <div className="modal-secure-left">
                <div className="shield">🛡️</div>
                <div>
                  <div className="modal-secure-title">Paiement sécurisé</div>
                  <div className="modal-secure-sub">Traitement par Stripe • Données chiffrées</div>
                </div>
              </div>
              <div className="cards-accepted" aria-label="Cartes acceptées">
                <span className="card visa">VISA</span>
                <span className="card mc">Mastercard</span>
                <span className="card amex">Amex</span>
                <span className="card cb">CB</span>
              </div>
            </div>

            {/* Stripe Elements avec apparence moderne */}
            {stripePromise ? (
              <Elements
                stripe={stripePromise}
                options={{
                  locale: "fr",
                  appearance: {
                    theme: "stripe",
                    labels: "floating",
                    variables: {
                      colorPrimary: "#2563eb",
                      colorText: "#0f172a",
                      colorBackground: "#ffffff",
                      borderRadius: "12px",
                    },
                  },
                }}
              >
                <CheckoutForm
                  priceId={selectedPriceId}
                  planName={selectedPlanName}
                  planPrice={selectedPlanPrice}
                  onClose={() => setShowModal(false)}
                  refetchAbonnement={() => window.location.reload()}
                />
              </Elements>
            ) : null}

            {/* Pied de modale : étiquettes confiance */}
            <div className="trust-row">
              <div className="trust-item">🔐 Conformité PCI-DSS</div>
              <div className="trust-item">🧩 3D Secure 2.0</div>
              <div className="trust-item">🌍 Stripe, leader mondial</div>
              <div className="trust-item">🕵️ Aucune carte stockée chez nous</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanCard({
  title, features, price,
  onSelect, badge, highlight, highlightPremium, isCurrent,
  paymentEnabled, isSelecting,
}) {
  return (
    <div className={`abonnement-card ${highlight ? 'highlight' : ''} ${highlightPremium ? 'premium-style' : ''}`}>
      {badge && <div className="badge">{badge}</div>}
      {highlightPremium && <div className="premium-badge">💎 Premium</div>}
      <h2>{title}</h2>
      <ul>
        {features.map((f, idx) => (
          <li key={idx}>{f}</li>
        ))}
      </ul>
      <div className="pricing-box">
        <p className="plan-price">
          <span className="current-price">{price}</span>
          {paymentEnabled ? " — paiement unique" : " — tarif indicatif (aucun débit en test)"}
        </p>
      </div>
      {isCurrent ? (
        <button disabled className="abonnement-current-btn">Abonnement actuel</button>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="choose-offer-btn"
          disabled={isSelecting}
        >
          {isSelecting ? "Enregistrement…" : paymentEnabled ? "Choisir cette offre" : "Activer cette formule"}
        </button>
      )}
    </div>
  );
}

function CheckoutForm({ priceId, planName, planPrice, onClose, refetchAbonnement }) {
  // Recalcule la base API ici aussi (composant séparé)
  const RAW_API_INNER =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
    process.env.VITE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "";
  const API_BASE_INNER = (
    RAW_API_INNER
      ? RAW_API_INNER.replace(/\/+$/, "").replace(/\/api$/i, "")
      : (typeof window !== "undefined" && window.location.origin) || ""
  );

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete || loading) return;
    setLoading(true);
    setCardError("");

    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${API_BASE_INNER}/api/stripe/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ priceId }),
      });
      if (!res.ok) throw new Error("create-payment-intent failed");
      const { clientSecret } = await res.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: `${firstName} ${lastName}`.trim() },
        },
      });

      if (result.error) {
        setCardError(result.error.message || "Paiement refusé, vérifiez la carte.");
      } else if (result.paymentIntent.status === "succeeded") {
        // Met à jour l'abonnement côté back
        const resAbonnement = await fetch(`${API_BASE_INNER}/api/users/update-abonnement`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({ abonnement: planName.toLowerCase() }),
        });

        const resAbonnementData = await resAbonnement.json().catch(() => ({}));
        if (!resAbonnement.ok) {
          setCardError(resAbonnementData.message || "Erreur lors de la mise à jour de l’abonnement.");
          setLoading(false);
          return;
        }

        localStorage.setItem("abonnement", planName.toLowerCase());
        if (refetchAbonnement) refetchAbonnement();
        onClose();
        navigate("/pro/calendar");
      }
    } catch (err) {
      setCardError("Une erreur est survenue lors du paiement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-form">
      <div className="summary-line">
        <div>
          <h3 className="offer-title">🧾 Offre sélectionnée : {planName}</h3>
          <p className="offer-price"><strong>Prix :</strong> {planPrice} (paiement unique)</p>
        </div>
        <div className="secure-chip">
          <span className="lock">🔒</span> Paiement chiffré
        </div>
      </div>

      <div className="payer-fields">
        <div className="field">
          <label>Prénom</label>
          <input
            type="text"
            placeholder="Jean"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoComplete="given-name"
          />
        </div>
        <div className="field">
          <label>Nom</label>
          <input
            type="text"
            placeholder="Dupont"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="field">
        <label>Carte bancaire</label>
        <div className="card-element-wrapper">
          <CardElement
            onChange={(e) => {
              setCardComplete(e.complete);
              setCardError(e.error?.message || "");
            }}
            options={{
              hidePostalCode: true,
              iconStyle: "solid",
              style: {
                base: {
                  fontSize: "16px",
                  lineHeight: "24px",
                },
              },
            }}
          />
          <div className="card-logos" aria-hidden>
            <span className="mini-card visa" />
            <span className="mini-card mc" />
            <span className="mini-card amex" />
            <span className="mini-card cb" />
          </div>
        </div>
        {cardError ? <div className="card-error">{cardError}</div> : null}
      </div>

      <div className="secure-notes">
        <div>🛡️ <b>Stripe</b> traite le paiement, nous n’accédons jamais à vos informations de carte.</div>
        <div>✅ 3D Secure 2.0 peut être demandé par votre banque pour sécuriser l’opération.</div>
      </div>

      <div className="cta-row">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
          Annuler
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={!stripe || !cardComplete || loading || !firstName || !lastName}
        >
          {loading ? "Traitement…" : "Valider le paiement"}
        </button>
      </div>

      <div className="stripe-powered">
        <span className="powered-by">Propulsé par</span>
        <span className="stripe-badge">Stripe</span>
      </div>
    </form>
  );
}

export default Abonnement;
